/**
 * SVG path import/export for fusebox "svg" layers.
 *
 * An svg layer (a fuse of kind.type 'svg') stores its geometry as `svgPaths`: an array of SVG `d`
 * strings, ONE SUBPATH PER ENTRY, all rendered inside a single <path fill-rule="evenodd"> so that
 * inner subpaths become holes. Coordinates are normalised to a 100-unit-wide box; `svgViewBoxH` is
 * the box height in those units.
 *
 * The path editor (FuseboxSVGPathEditor) does not edit curves: its model is a polyline of vertices,
 * each with an optional corner `radius` (a circular fillet). `buildD` serialises that model and this
 * module's `parseSvgPath` maps ANY SVG path (absolute/relative commands, H/V, C/S/Q/T/A, implicit
 * repeats, several subpaths) back onto it:
 *   - a curve that bends once like a rounded corner (a Q always; a C or a circular A whose end
 *     tangents intersect ahead of the start and behind the end) becomes a vertex at the tangent
 *     intersection (for a Q: its control point) with radius = distance to the nearest endpoint;
 *   - the curve endpoints are kept as sharp vertices unless they lie on the straight edge between
 *     their neighbours (that is what buildD's own output looks like, so stored paths round-trip);
 *   - any other curve (S-curves, large arcs, ellipses) is flattened into line segments.
 *
 * Pure module (no React/DOM imports) so it can be unit-tested in node; `parseSvgDocument` needs a
 * browser `DOMParser` at call time.
 */

const EPS = 1e-6;
// sin() of the max angle at which three points still count as collinear (~0.3°)
const COLLINEAR_TOLERANCE = 5e-3;
const IDENTITY = [1, 0, 0, 1, 0, 0];

/* ------------------------------------------------------------------ */
/*  Geometry helpers                                                    */
/* ------------------------------------------------------------------ */

export function dist(a, b) {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function lerp(a, b, d) {
  const len = dist(a, b);
  if (len === 0) return a;
  const t = d / len;
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function samePoint(a, b) {
  return Math.abs(a.x - b.x) < EPS && Math.abs(a.y - b.y) < EPS;
}

function sub(a, b) {
  return { x: a.x - b.x, y: a.y - b.y };
}

function isZero(v) {
  return Math.hypot(v.x, v.y) < EPS;
}

// True when `p` lies on the straight segment prev→next (same direction, tiny angle)
function isBetweenCollinear(prev, p, next) {
  const d1 = sub(p, prev);
  const d2 = sub(next, p);
  const l1 = Math.hypot(d1.x, d1.y);
  const l2 = Math.hypot(d2.x, d2.y);
  if (l1 < EPS || l2 < EPS) return true;
  const cross = d1.x * d2.y - d1.y * d2.x;
  const dot = d1.x * d2.x + d1.y * d2.y;
  return dot > 0 && Math.abs(cross) / (l1 * l2) < COLLINEAR_TOLERANCE;
}

/* ------------------------------------------------------------------ */
/*  Vertex model → SVG `d` string                                      */
/* ------------------------------------------------------------------ */

export function buildD(points, closed) {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  const n = points.length;

  // Pass 1: initial clamped radii (just by individual edge lengths)
  const r = points.map((p, i) => {
    if (!p.radius) return 0;
    const prev = i === 0 ? (closed ? points[n - 1] : null) : points[i - 1];
    const next = i === n - 1 ? (closed ? points[0] : null) : points[i + 1];
    if (!prev || !next) return 0;
    return Math.min(p.radius, dist(p, prev), dist(p, next));
  });

  // Pass 2: for each edge, if the two corners overlap, scale them down
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    if (!closed && j === 0) continue;
    const d = dist(points[i], points[j]);
    const sum = r[i] + r[j];
    if (sum > d && sum > 0) {
      const scale = d / sum;
      r[i] *= scale;
      r[j] *= scale;
    }
  }

  // Pass 3: build path
  let d = "";
  for (let i = 0; i < n; i++) {
    const p = points[i];
    const prev = i === 0 ? (closed ? points[n - 1] : null) : points[i - 1];
    const next = i === n - 1 ? (closed ? points[0] : null) : points[i + 1];

    if (!prev || !next || !r[i]) {
      d += i === 0 ? `M ${p.x.toFixed(4)} ${p.y.toFixed(4)} ` : `L ${p.x.toFixed(4)} ${p.y.toFixed(4)} `;
    } else {
      const p1 = lerp(p, prev, r[i]);
      const p2 = lerp(p, next, r[i]);

      // The arc command needs the actual circular radius, not the corner offset distance.
      // For a circle inscribed in the corner, arcRadius = r × tan(halfAngle).
      // The cross product determines which side of the edge the center falls on,
      // giving us the correct sweep direction for convex vs reflex angles.
      const ux = prev.x - p.x, uy = prev.y - p.y;
      const nx = next.x - p.x, ny = next.y - p.y;
      const dot = ux * nx + uy * ny;
      const cross = ux * ny - uy * nx;
      const cosVal = Math.max(-1, Math.min(1, dot / (Math.hypot(ux, uy) * Math.hypot(nx, ny))));
      const angle = Math.acos(cosVal);
      const arcR = r[i] * Math.tan(angle / 2);

      d += i === 0 ? `M ${p1.x.toFixed(4)} ${p1.y.toFixed(4)} ` : `L ${p1.x.toFixed(4)} ${p1.y.toFixed(4)} `;
      // Near-straight vertices make tan(angle/2) explode; fall back to a line.
      if (!isFinite(arcR) || arcR > 1e6) {
        d += `L ${p2.x.toFixed(4)} ${p2.y.toFixed(4)} `;
      } else {
        const sweep = cross < 0 ? 1 : 0;
        d += `A ${arcR.toFixed(4)} ${arcR.toFixed(4)} 0 0 ${sweep} ${p2.x.toFixed(4)} ${p2.y.toFixed(4)} `;
      }
    }
  }
  if (closed) d += "Z";
  return d;
}

/* ------------------------------------------------------------------ */
/*  SVG path grammar → absolute segments                               */
/* ------------------------------------------------------------------ */

const ARG_COUNT = { M: 2, L: 2, H: 1, V: 1, C: 6, S: 4, Q: 4, T: 2, A: 7, Z: 0 };

/**
 * Tokenizes an SVG path `d` attribute into explicit commands: implicit repeats are expanded
 * ("M 0 0 10 10" → M + L), numbers may be glued ("10-5", "1.5.5", "1e-3"), arc flags may be glued
 * ("0 0110 10"). Throws on malformed data.
 * @returns {{cmd: string, args: number[]}[]}
 */
export function parsePathCommands(d) {
  const s = String(d || '');
  const n = s.length;
  let i = 0;
  const out = [];

  const isWs = c => c === ' ' || c === ',' || c === '\n' || c === '\r' || c === '\t';
  const isDigit = c => c >= '0' && c <= '9';
  const skipWs = () => { while (i < n && isWs(s[i])) i++; };
  const fail = () => { throw new Error(`Invalid SVG path data near "${s.slice(Math.max(0, i - 5), i + 10)}"`); };

  const readNumber = () => {
    skipWs();
    const start = i;
    if (s[i] === '+' || s[i] === '-') i++;
    while (i < n && isDigit(s[i])) i++;
    if (s[i] === '.') {
      i++;
      while (i < n && isDigit(s[i])) i++;
    }
    if (s[i] === 'e' || s[i] === 'E') {
      let j = i + 1;
      if (s[j] === '+' || s[j] === '-') j++;
      if (isDigit(s[j])) {
        i = j;
        while (i < n && isDigit(s[i])) i++;
      }
    }
    const v = parseFloat(s.slice(start, i));
    if (!Number.isFinite(v)) fail();
    return v;
  };

  const readFlag = () => {
    skipWs();
    if (s[i] === '0' || s[i] === '1') return +s[i++];
    return fail();
  };

  let cmd = null;
  while (i < n) {
    skipWs();
    if (i >= n) break;

    if (/[MmLlHhVvCcSsQqTtAaZz]/.test(s[i])) {
      cmd = s[i++];
    } else if (!cmd || cmd === 'Z' || cmd === 'z') {
      fail();
    }

    const count = ARG_COUNT[cmd.toUpperCase()];
    const args = [];
    for (let k = 0; k < count; k++) {
      const isArcFlag = (cmd === 'A' || cmd === 'a') && (k === 3 || k === 4);
      args.push(isArcFlag ? readFlag() : readNumber());
    }
    out.push({ cmd, args });

    // Subsequent implicit coordinates after a moveto are linetos
    if (cmd === 'M') cmd = 'L';
    else if (cmd === 'm') cmd = 'l';
  }
  return out;
}

/**
 * Resolves relative commands, H/V, S/T reflections and subpath structure.
 * @returns {{start: {x,y}, segs: object[], closed: boolean}[]} segments are one of
 *   {type:'L', to} | {type:'Q', c, to} | {type:'C', c1, c2, to} | {type:'A', rx, ry, rot, large, sweep, to}
 */
export function pathCommandsToSubpaths(commands) {
  const subpaths = [];
  let sp = null;
  let cur = { x: 0, y: 0 };
  let start = { x: 0, y: 0 };
  let prevC = null; // last cubic control point, for S
  let prevQ = null; // last quadratic control point, for T

  const newSubpath = (at) => {
    sp = { start: at, segs: [], closed: false };
    subpaths.push(sp);
  };

  for (const { cmd, args } of commands) {
    const U = cmd.toUpperCase();
    const rel = cmd !== U;
    const ox = rel ? cur.x : 0;
    const oy = rel ? cur.y : 0;
    const pt = (x, y) => ({ x: ox + x, y: oy + y });

    if (U === 'M') {
      cur = pt(args[0], args[1]);
      start = cur;
      newSubpath(cur);
      prevC = prevQ = null;
      continue;
    }

    if (U === 'Z') {
      if (sp) sp.closed = true;
      cur = start;
      prevC = prevQ = null;
      continue;
    }

    let seg;
    switch (U) {
      case 'L': seg = { type: 'L', to: pt(args[0], args[1]) }; break;
      case 'H': seg = { type: 'L', to: { x: ox + args[0], y: cur.y } }; break;
      case 'V': seg = { type: 'L', to: { x: cur.x, y: oy + args[0] } }; break;
      case 'C': seg = { type: 'C', c1: pt(args[0], args[1]), c2: pt(args[2], args[3]), to: pt(args[4], args[5]) }; break;
      case 'S': {
        const c1 = prevC ? { x: 2 * cur.x - prevC.x, y: 2 * cur.y - prevC.y } : cur;
        seg = { type: 'C', c1, c2: pt(args[0], args[1]), to: pt(args[2], args[3]) };
        break;
      }
      case 'Q': seg = { type: 'Q', c: pt(args[0], args[1]), to: pt(args[2], args[3]) }; break;
      case 'T': {
        const c = prevQ ? { x: 2 * cur.x - prevQ.x, y: 2 * cur.y - prevQ.y } : cur;
        seg = { type: 'Q', c, to: pt(args[0], args[1]) };
        break;
      }
      case 'A':
        seg = {
          type: 'A', rx: Math.abs(args[0]), ry: Math.abs(args[1]), rot: args[2],
          large: !!args[3], sweep: !!args[4], to: pt(args[5], args[6])
        };
        break;
      default:
        throw new Error(`Unsupported path command ${cmd}`);
    }

    // A drawing command right after a closepath starts a new subpath at the same initial point
    if (!sp || sp.closed) newSubpath(cur);

    sp.segs.push(seg);
    prevC = seg.type === 'C' ? seg.c2 : null;
    prevQ = seg.type === 'Q' ? seg.c : null;
    cur = seg.to;
  }

  return subpaths.filter(p => p.segs.length);
}

/* ------------------------------------------------------------------ */
/*  Curves → corner vertices (or flattened points)                     */
/* ------------------------------------------------------------------ */

// Intersection of the end tangents of a curve a→e. Returns null unless it is AHEAD of a and
// BEHIND e along their tangents, i.e. the curve bends once like a rounded corner.
function tangentIntersection(a, ta, e, te) {
  const denom = ta.x * te.y - ta.y * te.x;
  if (Math.abs(denom) < 1e-9 * Math.hypot(ta.x, ta.y) * Math.hypot(te.x, te.y)) return null; // parallel
  const t = ((e.x - a.x) * te.y - (e.y - a.y) * te.x) / denom;
  if (t <= 0) return null;
  const v = { x: a.x + t * ta.x, y: a.y + t * ta.y };
  const s = (v.x - e.x) * te.x + (v.y - e.y) * te.y;
  if (s >= 0) return null;
  return v;
}

// SVG arc endpoint → center parametrization (SVG spec appendix F.6.5)
function arcToCenter(a, seg) {
  let { rx, ry } = seg;
  const { rot, large, sweep, to: e } = seg;
  if (samePoint(a, e) || rx < EPS || ry < EPS) return null;

  const phi = (rot || 0) * Math.PI / 180;
  const cos = Math.cos(phi), sin = Math.sin(phi);
  const dx = (a.x - e.x) / 2, dy = (a.y - e.y) / 2;
  const x1 = cos * dx + sin * dy;
  const y1 = -sin * dx + cos * dy;

  const lambda = (x1 * x1) / (rx * rx) + (y1 * y1) / (ry * ry);
  if (lambda > 1) {
    const s = Math.sqrt(lambda);
    rx *= s;
    ry *= s;
  }

  const sign = large === sweep ? -1 : 1;
  const num = rx * rx * ry * ry - rx * rx * y1 * y1 - ry * ry * x1 * x1;
  const den = rx * rx * y1 * y1 + ry * ry * x1 * x1;
  const coef = sign * Math.sqrt(Math.max(0, num / den));
  const cxp = coef * (rx * y1 / ry);
  const cyp = coef * (-ry * x1 / rx);
  const cx = cos * cxp - sin * cyp + (a.x + e.x) / 2;
  const cy = sin * cxp + cos * cyp + (a.y + e.y) / 2;

  const angleBetween = (ux, uy, vx, vy) => {
    const d = ux * vx + uy * vy;
    const l = Math.hypot(ux, uy) * Math.hypot(vx, vy);
    let ang = Math.acos(Math.max(-1, Math.min(1, d / l)));
    if (ux * vy - uy * vx < 0) ang = -ang;
    return ang;
  };
  const theta1 = angleBetween(1, 0, (x1 - cxp) / rx, (y1 - cyp) / ry);
  let dtheta = angleBetween((x1 - cxp) / rx, (y1 - cyp) / ry, (-x1 - cxp) / rx, (-y1 - cyp) / ry);
  if (!sweep && dtheta > 0) dtheta -= 2 * Math.PI;
  else if (sweep && dtheta < 0) dtheta += 2 * Math.PI;

  const pointAt = (t) => {
    const th = theta1 + t * dtheta;
    const px = rx * Math.cos(th), py = ry * Math.sin(th);
    return { x: cos * px - sin * py + cx, y: sin * px + cos * py + cy };
  };
  const tangentAt = (t) => {
    const th = theta1 + t * dtheta;
    const s = Math.sign(dtheta) || 1;
    const px = -rx * Math.sin(th) * s, py = ry * Math.cos(th) * s;
    return { x: cos * px - sin * py, y: sin * px + cos * py };
  };

  return { cx, cy, rx, ry, theta1, dtheta, pointAt, tangentAt };
}

/** Rounded-corner interpretation of a curve starting at `a`, or null if it is not corner-shaped */
function curveCorner(a, seg) {
  const e = seg.to;
  if (samePoint(a, e)) return null;

  let v = null;
  if (seg.type === 'Q') {
    // The control point IS the corner vertex, and both endpoints lie on its tangent lines
    if (samePoint(seg.c, a) || samePoint(seg.c, e)) return null;
    v = seg.c;
  } else if (seg.type === 'C') {
    let ta = sub(seg.c1, a);
    if (isZero(ta)) ta = sub(seg.c2, a);
    let te = sub(e, seg.c2);
    if (isZero(te)) te = sub(e, seg.c1);
    if (isZero(ta) || isZero(te)) return null;
    v = tangentIntersection(a, ta, e, te);
  } else if (seg.type === 'A') {
    const arc = arcToCenter(a, seg);
    if (!arc || seg.large) return null;
    // Only circular arcs are fillets; elliptical ones get flattened
    if (Math.abs(arc.rx - arc.ry) > 0.02 * Math.max(arc.rx, arc.ry)) return null;
    v = tangentIntersection(a, arc.tangentAt(0), e, arc.tangentAt(1));
  }
  if (!v) return null;

  const radius = Math.min(dist(a, v), dist(e, v));
  if (radius < EPS) return null;
  return { v, radius };
}

function bezierAt(a, seg, t) {
  const mt = 1 - t;
  if (seg.type === 'Q') {
    return {
      x: mt * mt * a.x + 2 * mt * t * seg.c.x + t * t * seg.to.x,
      y: mt * mt * a.y + 2 * mt * t * seg.c.y + t * t * seg.to.y,
    };
  }
  return {
    x: mt * mt * mt * a.x + 3 * mt * mt * t * seg.c1.x + 3 * mt * t * t * seg.c2.x + t * t * t * seg.to.x,
    y: mt * mt * mt * a.y + 3 * mt * mt * t * seg.c1.y + 3 * mt * t * t * seg.c2.y + t * t * t * seg.to.y,
  };
}

/** Flattens a non-corner curve into a polyline (end point included, start excluded) */
function flattenCurve(a, seg) {
  const out = [];
  if (seg.type === 'Q' || seg.type === 'C') {
    const N = 8;
    for (let k = 1; k <= N; k++) out.push(bezierAt(a, seg, k / N));
  } else if (seg.type === 'A') {
    const arc = arcToCenter(a, seg);
    if (!arc) return [seg.to];
    const N = Math.max(2, Math.ceil(Math.abs(arc.dtheta) / (Math.PI / 8)));
    for (let k = 1; k < N; k++) out.push(arc.pointAt(k / N));
    out.push(seg.to);
  } else {
    out.push(seg.to);
  }
  return out;
}

function subpathToVertices(sp) {
  const pts = [];
  // curveEnd marks endpoints of corner curves: they are dropped later if they turn out to be
  // redundant (lying on the straight edge between their neighbours)
  const push = (p, radius = 0, curveEnd = false) => {
    const last = pts[pts.length - 1];
    if (last && samePoint(last, p)) {
      last.radius = Math.max(last.radius, radius);
      last.curveEnd = last.curveEnd || curveEnd;
      return;
    }
    pts.push({ x: p.x, y: p.y, radius, curveEnd });
  };

  push(sp.start);
  let cur = sp.start;
  for (const seg of sp.segs) {
    if (seg.type === 'L') {
      push(seg.to);
    } else {
      const corner = curveCorner(cur, seg);
      if (corner) {
        const last = pts[pts.length - 1];
        if (last && samePoint(last, cur)) last.curveEnd = true;
        push(corner.v, corner.radius);
        push(seg.to, 0, true);
      } else {
        for (const p of flattenCurve(cur, seg)) push(p);
      }
    }
    cur = seg.to;
  }

  // Closed paths that end where they started: merge the duplicate (the start point is then also the
  // end of the last curve, e.g. a <rect rx> path starting right after its last corner arc)
  if (sp.closed && pts.length > 1 && samePoint(pts[0], pts[pts.length - 1])) {
    const last = pts.pop();
    pts[0].radius = Math.max(pts[0].radius, last.radius);
    pts[0].curveEnd = pts[0].curveEnd || last.curveEnd;
  }

  const n = pts.length;
  const points = [];
  for (let i = 0; i < n; i++) {
    const p = pts[i];
    if (p.curveEnd && !p.radius && n > 2) {
      const prev = i > 0 ? pts[i - 1] : (sp.closed ? pts[n - 1] : null);
      const next = i < n - 1 ? pts[i + 1] : (sp.closed ? pts[0] : null);
      if (prev && next && isBetweenCollinear(prev, p, next)) continue;
    }
    points.push({ x: p.x, y: p.y, radius: p.radius });
  }

  return { points, isClosed: sp.closed };
}

/**
 * Parses any SVG path `d` string into the editor's vertex model, one entry per subpath.
 * @returns {{points: {x:number, y:number, radius:number}[], isClosed: boolean}[]}
 */
export function parseSvgPath(d) {
  return pathCommandsToSubpaths(parsePathCommands(d))
    .map(subpathToVertices)
    .filter(sp => sp.points.length > 0);
}

/** Legacy single-subpath variant (stored svgPaths entries hold exactly one subpath) */
export function parsePathData(d) {
  return parseSvgPath(d)[0] || { points: [], isClosed: false };
}

/* ------------------------------------------------------------------ */
/*  Transforms                                                         */
/* ------------------------------------------------------------------ */

// Matrices are SVG-style [a b c d e f]: x' = a·x + c·y + e, y' = b·x + d·y + f
export function multiplyMatrix(m1, m2) {
  const [a1, b1, c1, d1, e1, f1] = m1;
  const [a2, b2, c2, d2, e2, f2] = m2;
  return [
    a1 * a2 + c1 * b2, b1 * a2 + d1 * b2,
    a1 * c2 + c1 * d2, b1 * c2 + d1 * d2,
    a1 * e2 + c1 * f2 + e1, b1 * e2 + d1 * f2 + f1
  ];
}

export function applyMatrix(m, p) {
  return { x: m[0] * p.x + m[2] * p.y + m[4], y: m[1] * p.x + m[3] * p.y + m[5] };
}

/** Parses an SVG `transform` attribute (matrix/translate/scale/rotate/skewX/skewY list) */
export function parseTransform(str) {
  let m = IDENTITY;
  if (!str) return m;
  const re = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g;
  let match;
  while ((match = re.exec(str))) {
    const args = match[2].trim().split(/[\s,]+/).filter(Boolean).map(Number);
    const rad = (args[0] || 0) * Math.PI / 180;
    let t = null;
    switch (match[1]) {
      case 'matrix': t = args; break;
      case 'translate': t = [1, 0, 0, 1, args[0] || 0, args[1] || 0]; break;
      case 'scale': t = [args[0], 0, 0, args.length > 1 ? args[1] : args[0], 0, 0]; break;
      case 'rotate': {
        const c = Math.cos(rad), s = Math.sin(rad);
        t = [c, s, -s, c, 0, 0];
        if (args.length > 2) {
          t = multiplyMatrix(multiplyMatrix([1, 0, 0, 1, args[1], args[2]], t), [1, 0, 0, 1, -args[1], -args[2]]);
        }
        break;
      }
      case 'skewX': t = [1, 0, Math.tan(rad), 1, 0, 0]; break;
      case 'skewY': t = [1, Math.tan(rad), 0, 1, 0, 0]; break;
      default: break;
    }
    if (t && t.length === 6 && t.every(Number.isFinite)) m = multiplyMatrix(m, t);
  }
  return m;
}

function transformSubpath(sp, m) {
  if (m === IDENTITY) return sp;
  // Radii are lengths: scale them by the geometric mean of the axis scales
  const radiusScale = Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));
  return {
    isClosed: sp.isClosed,
    points: sp.points.map(p => ({ ...applyMatrix(m, p), radius: p.radius * radiusScale }))
  };
}

/* ------------------------------------------------------------------ */
/*  SVG documents                                                      */
/* ------------------------------------------------------------------ */

const SKIP_CONTAINERS = new Set(['defs', 'clipPath', 'mask', 'symbol', 'marker', 'pattern', 'metadata', 'title', 'desc']);
const SHAPE_TAGS = new Set(['path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon']);

/** Converts basic shape elements into path data (only attribute values, no transforms) */
export function shapeElementToPathData(el) {
  const num = (name, def = 0) => {
    const v = parseFloat(el.getAttribute(name));
    return Number.isFinite(v) ? v : def;
  };

  switch (el.localName) {
    case 'path':
      return el.getAttribute('d');
    case 'rect': {
      const x = num('x'), y = num('y'), w = num('width'), h = num('height');
      if (w <= 0 || h <= 0) return null;
      let rx = num('rx', NaN), ry = num('ry', NaN);
      if (!Number.isFinite(rx) && !Number.isFinite(ry)) rx = ry = 0;
      else if (!Number.isFinite(rx)) rx = ry;
      else if (!Number.isFinite(ry)) ry = rx;
      rx = Math.min(Math.abs(rx), w / 2);
      ry = Math.min(Math.abs(ry), h / 2);
      if (rx < EPS || ry < EPS) return `M ${x} ${y} H ${x + w} V ${y + h} H ${x} Z`;
      const arc = `A ${rx} ${ry} 0 0 1`;
      return `M ${x + rx} ${y} H ${x + w - rx} ${arc} ${x + w} ${y + ry} V ${y + h - ry} ${arc} ${x + w - rx} ${y + h} `
        + `H ${x + rx} ${arc} ${x} ${y + h - ry} V ${y + ry} ${arc} ${x + rx} ${y} Z`;
    }
    case 'circle':
    case 'ellipse': {
      const cx = num('cx'), cy = num('cy');
      const rx = el.localName === 'circle' ? num('r') : num('rx');
      const ry = el.localName === 'circle' ? num('r') : num('ry');
      if (rx <= 0 || ry <= 0) return null;
      const arc = `A ${rx} ${ry} 0 0 1`;
      return `M ${cx + rx} ${cy} ${arc} ${cx} ${cy + ry} ${arc} ${cx - rx} ${cy} ${arc} ${cx} ${cy - ry} ${arc} ${cx + rx} ${cy} Z`;
    }
    case 'line':
      return `M ${num('x1')} ${num('y1')} L ${num('x2')} ${num('y2')}`;
    case 'polyline':
    case 'polygon': {
      const nums = (el.getAttribute('points') || '').trim().split(/[\s,]+/).map(Number).filter(Number.isFinite);
      if (nums.length < 4) return null;
      const pairs = [];
      for (let i = 0; i + 1 < nums.length; i += 2) pairs.push(`${nums[i]} ${nums[i + 1]}`);
      return `M ${pairs[0]} L ${pairs.slice(1).join(' L ')}${el.localName === 'polygon' ? ' Z' : ''}`;
    }
    default:
      return null;
  }
}

function getViewBox(root) {
  const vb = (root.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
  if (vb.length === 4 && vb.every(Number.isFinite) && vb[2] > 0 && vb[3] > 0) {
    return { x: vb[0], y: vb[1], width: vb[2], height: vb[3] };
  }
  const width = parseFloat(root.getAttribute('width'));
  const height = parseFloat(root.getAttribute('height'));
  if (width > 0 && height > 0) return { x: 0, y: 0, width, height };
  return null;
}

/**
 * Parses SVG markup into one shape per drawable element (<path>, <rect>, <circle>, <ellipse>,
 * <line>, <polyline>, <polygon>), with the element's and its ancestors' transforms applied.
 * Elements inside <defs>/<clipPath>/<mask>/<symbol>/<marker>/<pattern> are ignored; <use> is not supported.
 *
 * @returns {{shapes: {label: string, tag: string, subpaths: object[], bbox: object}[], viewBox: object|null}}
 */
export function parseSvgDocument(svgText) {
  let markup = String(svgText || '').trim();
  if (!/<svg[\s>]/i.test(markup)) {
    // Bare <path .../> or <g>...</g> snippets
    markup = `<svg xmlns="http://www.w3.org/2000/svg">${markup}</svg>`;
  }

  const doc = new DOMParser().parseFromString(markup, 'image/svg+xml');
  const root = doc.documentElement;
  if (!root || root.localName !== 'svg' || doc.getElementsByTagName('parsererror').length) {
    throw new Error('The text is not valid SVG markup');
  }

  const shapes = [];
  let counter = 0;

  const walk = (el, matrix) => {
    for (const child of Array.from(el.children || [])) {
      const tag = child.localName;
      if (SKIP_CONTAINERS.has(tag)) continue;

      const m = multiplyMatrix(matrix, parseTransform(child.getAttribute('transform')));

      if (!SHAPE_TAGS.has(tag)) {
        walk(child, m);
        continue;
      }

      counter++;
      const d = shapeElementToPathData(child);
      if (!d) continue;

      let subpaths;
      try {
        subpaths = parseSvgPath(d);
      } catch (err) {
        console.warn(`Skipping <${tag}> #${counter}: ${err.message}`);
        continue;
      }
      subpaths = subpaths.map(sp => transformSubpath(sp, m)).filter(sp => sp.points.length >= 2);
      if (!subpaths.length) continue;

      shapes.push({
        label: child.getAttribute('id') || child.getAttribute('inkscape:label') || `${tag} #${counter}`,
        tag,
        subpaths,
        bbox: subpathsBBox(subpaths)
      });
    }
  };

  walk(root, IDENTITY);

  return { shapes, viewBox: getViewBox(root) };
}

/** Accepts SVG markup or a bare path `d` string */
export function parseSvgSource(text) {
  const t = String(text || '').trim();
  if (t.startsWith('<')) return parseSvgDocument(t);

  const subpaths = parseSvgPath(t).filter(sp => sp.points.length >= 2);
  if (!subpaths.length) return { shapes: [], viewBox: null };
  return { shapes: [{ label: 'path', tag: 'path', subpaths, bbox: subpathsBBox(subpaths) }], viewBox: null };
}

/* ------------------------------------------------------------------ */
/*  Clipboard / drop helpers                                           */
/* ------------------------------------------------------------------ */

/** @returns {'svg'|'path'|'url'|null} what a pasted/dropped text looks like */
export function classifyPastedText(text) {
  const t = String(text || '').trim();
  if (!t) return null;
  if (/^(<\?xml|<!doctype\s+svg|<svg[\s>]|<path[\s>]|<g[\s>])/i.test(t)) return 'svg';
  if (/^https?:\/\/\S+\.svg(\?\S*)?$/i.test(t)) return 'url';
  if (/^[Mm]\s*[-+.\d]/.test(t) && /^[MmLlHhVvCcSsQqTtAaZz\d\s,.+\-eE]+$/.test(t)) return 'path';
  return null;
}

export function isSvgFile(file) {
  return !!file && (file.type === 'image/svg+xml' || /\.svg$/i.test(file.name || ''));
}

/* ------------------------------------------------------------------ */
/*  Layers                                                             */
/* ------------------------------------------------------------------ */

export function subpathsBBox(subpaths) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const sp of subpaths) {
    for (const p of sp.points) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  if (minX === Infinity) return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

/**
 * Normalises subpaths (in any coordinate space) into an svg layer payload: `svgPaths` (one `d`
 * per subpath, 100 units = layer width) and `svgViewBoxH`. `bbox` is in the source space.
 */
export function buildSvgLayer(subpaths) {
  const bbox = subpathsBBox(subpaths);
  const scale = 100 / Math.max(bbox.width, EPS);
  const svgPaths = subpaths
    .map(sp => buildD(sp.points.map(p => ({
      x: (p.x - bbox.minX) * scale,
      y: (p.y - bbox.minY) * scale,
      radius: p.radius * scale
    })), sp.isClosed))
    .filter(Boolean);

  return { svgPaths, svgViewBoxH: Math.max(bbox.height * scale, 1), bbox };
}
